import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { useBrainStore } from '@/state/brainStore';
import { useSessionStore } from '@/state/sessionStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useActiveStudio } from '@/state/useActiveStudio';
import { isAllStudios } from '@/domain/studioScope';
import InfoTip from '@/components/InfoTip';
import { aud } from '@/utils/format';
import { arrangementLabel, finishTime } from '@/screens/payouts/payoutLabels';
import {
  dayCalculation,
  depositsReceived,
  depositsReceivedBreakdown,
  merchBreakdown,
  reconciliationDaysFor,
  type CalcArtistRow,
} from '@/domain/reconciliationFlow';
import type { DepositChannel } from '@/domain/types';
import { dayPayouts } from '@/domain/payoutFlow';
import { dayTurnover } from '@/domain/dayTurnover';
import { outstandingPay } from '@/domain/outstandingPay';
import OutstandingPayPanel from '@/components/finance/OutstandingPayPanel';
import type { Studio, StudioId } from '@/domain/types';
import { dayKey } from '@/utils/studioDay';

/**
 * Day sheet (Prompt E3; renamed from Calculations). The daily reconciliation
 * detail for one store on one day: turnover, the split between ours and the
 * artists', and the per-artist payout ledger. Every per-artist figure reads from
 * the E2 payout derivation (READ, DON'T RECOMPUTE) - the commission maths lives in
 * the engine, not here. The ranged roll-up, deposit pool and cash-timing views
 * live in the Money report (Reporting > Money); the Day sheet stays strictly one
 * day, which is what the Money report deep-links into.
 */

const SIGN_META: Record<CalcArtistRow['signState'], { label: string; pill: string }> = {
  signed: { label: 'Signed', pill: 'pill-mint' },
  deferred: { label: 'Deferred', pill: 'pill-blood' },
  due: { label: 'Payable', pill: 'pill-gold' },
  'in-chair': { label: 'In chair', pill: 'pill-cream' },
};

const CHANNEL_LABEL: Record<DepositChannel, string> = {
  card: 'Card',
  transfer: 'Transfer',
  cash: 'Cash',
  link: 'Link',
};

const dayLabel = (dayIso: string): string => format(new Date(`${dayIso}T12:00:00Z`), 'EEE d MMM');

export default function Calculations() {
  const studios = useBrainStore((s) => s.studios);
  const { activeStudio, assignedStudios } = useActiveStudio();
  const activeStudios = Object.values(studios).filter((s) => !s.archived);
  // Per-studio day sheet: 'all' does not aggregate here, so fall back to the
  // persona's first studio when the switcher is network-wide.
  const studioId = (isAllStudios(activeStudio)
    ? (assignedStudios[0] ?? 'coburg')
    : activeStudio) as StudioId;
  const studio = studios[studioId] ?? activeStudios[0];
  // A hard load / refresh mounts this route before /api/bootstrap has filled
  // `studios` (tv-2na.2). Show a loading shell until the studio resolves rather
  // than deriving the day from an undefined studio (which white-screened).
  if (!studio) {
    return (
      <section className="px-8 py-8" data-testid="calculations-loading">
        <p className="font-serif text-cream-soft">Loading day sheet…</p>
      </section>
    );
  }
  return <CalculationsScreen studio={studio} />;
}

function CalculationsScreen({ studio }: { studio: Studio }) {
  const studios = useBrainStore((s) => s.studios);
  const bookings = useBrainStore((s) => s.bookings);
  const payoutRecords = useBrainStore((s) => s.payoutRecords);
  const artists = useBrainStore((s) => s.artists);
  const salePayments = useBrainStore((s) => s.salePayments);
  const depositsStore = useBrainStore((s) => s.deposits);
  const merchItems = useBrainStore((s) => s.merchItems);
  const customers = useBrainStore((s) => s.customers);
  const clockIso = useSimulationStore((s) => s.clockIso);
  const { assignedStudios } = useActiveStudio();
  const setActiveStudio = useSessionStore((s) => s.setActiveStudio);

  const activeStudios = Object.values(studios).filter((s) => !s.archived);

  const days = useMemo(
    () => reconciliationDaysFor(studio.id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [studio.id, bookings, payoutRecords],
  );
  const simDay = dayKey(clockIso);
  const [day, setDay] = useState<string | null>(null);
  const [showMerch, setShowMerch] = useState(false);
  const [showDeposits, setShowDeposits] = useState(false);
  const dayIso =
    day && days.includes(day) ? day : days.includes(simDay) ? simDay : (days[0] ?? simDay);

  const calc = useMemo(
    () => dayCalculation(studio.id, dayIso),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [studio.id, dayIso, bookings, payoutRecords, artists, salePayments],
  );
  const depReceived = useMemo(
    () => depositsReceived(studio.id, dayIso),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [studio.id, dayIso, depositsStore, customers],
  );
  const merchRows = useMemo(
    () => merchBreakdown(studio.id, dayIso),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [studio.id, dayIso, salePayments, merchItems],
  );
  const depRows = useMemo(
    () => depositsReceivedBreakdown(studio.id, dayIso),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [studio.id, dayIso, depositsStore, customers],
  );
  const arrangementByArtist = useMemo(
    () => new Map(dayPayouts(studio.id, dayIso).map((p) => [p.artistId, p.arrangement])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [studio.id, dayIso, bookings, payoutRecords, artists],
  );

  // W7 n4: the tiles read the SAME completed-turnover helper as Reporting > Money
  // (checkout day, sale gross), so the two screens agree; in-chair work is shown
  // only on the sub-line.
  const turnover = useMemo(
    () => dayTurnover(studio.id, dayIso),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [studio.id, dayIso, bookings, artists, salePayments],
  );
  const owedStudioIds = useMemo(
    () => activeStudios.filter((s) => assignedStudios.includes(s.id)).map((s) => s.id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [studios, assignedStudios],
  );
  const owed = useMemo(
    () => outstandingPay(owedStudioIds, dayIso),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [owedStudioIds, dayIso, clockIso, bookings, payoutRecords, artists],
  );

  const oursPct = turnover.completedAud > 0 ? Math.round((turnover.oursAud / turnover.completedAud) * 100) : 0;

  return (
    <section className="px-8 py-8" data-testid="calculations">
      <header className="flex items-end justify-between max-w-7xl flex-wrap gap-4">
        <div>
          <p className="label-small-caps text-gold-bright">Reporting &middot; detail</p>
          <h1 className="heading-display text-3xl text-cream mt-2">Day sheet</h1>
          <p className="font-serif text-cream-soft mt-3 max-w-2xl">
            One studio, one day: turnover, what&rsquo;s ours versus the artists&rsquo;, and the per-artist payout
            split, read straight from the signed payouts. The ranged roll-up and cash-timing views live in the{' '}
            <Link
              to="/reporting?tab=money"
              className="text-gold-bright underline decoration-gold-dim/50 hover:decoration-gold-bright"
              data-testid="daysheet-report-link"
            >
              Money report
            </Link>
            .
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="font-inter text-[10px] uppercase tracking-widest text-cream-soft">Studio</label>
          <select
            value={studio.id}
            onChange={(e) => {
              setActiveStudio(e.target.value as StudioId);
              setDay(null);
            }}
            className="bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-1.5 text-sm text-cream"
            data-testid="calculations-studio-filter"
          >
            {activeStudios
              .filter((s) => assignedStudios.includes(s.id))
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {s.brand} · {s.name}
                </option>
              ))}
          </select>
        </div>
      </header>

      {/* Day picker — the day figures are meaningless without one. */}
      {days.length > 0 ? (
        <div className="mt-6 flex flex-wrap items-center gap-2 max-w-7xl" data-testid="calc-day-picker">
          <span className="font-inter text-[10px] uppercase tracking-widest text-cream-faint mr-1">Day</span>
          {days.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDay(d)}
              data-testid={`calc-day-${d}`}
              className={clsx(
                'rounded-full border px-3.5 py-1.5 text-xs font-inter transition',
                d === dayIso
                  ? 'bg-gold-bright text-ink-black border-gold-bright font-semibold'
                  : 'border-gold-dim/50 text-cream-soft hover:bg-ink-soft/60',
              )}
            >
              {dayLabel(d)}
            </button>
          ))}
        </div>
      ) : null}

      <div className="mt-6 max-w-7xl space-y-5">
        {/* KPI tiles */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="calc-kpis">
          <div className="panel p-5 border-gold-bright/40" data-testid="calc-turnover">
            <p className="label-small-caps text-gold-bright inline-flex items-center gap-1">
              Turnover · completed · {dayLabel(dayIso)}
              <InfoTip>
                The day&apos;s commissionable takings — the sale gross across the sessions worked that day
                (merch excluded, since the studio keeps merch whole). A session running past midnight counts on
                the day it started, as the artist&apos;s pay does. The same figure as Reporting &gt; Money for
                this studio and day. Ours + Artists add up to this. Work still in the chair is not turnover
                until it is checked out, so it shows on the line below at its day rate.
              </InfoTip>
            </p>
            <p
              className="font-cinzel text-4xl leading-none mt-2 tabular-nums text-gold-bright"
              data-testid="calc-turnover-amount"
            >
              {aud(turnover.completedAud)}
            </p>
            <p className="font-inter text-xs text-cream-soft mt-2">
              {turnover.completedSessions} completed session{turnover.completedSessions === 1 ? '' : 's'}
            </p>
            {turnover.inChairAud > 0 ? (
              <p className="font-serif text-sm text-cream-soft mt-1" data-testid="calc-turnover-in-chair">
                + <span className="font-mono tabular-nums">{aud(turnover.inChairAud)}</span> still in the chair
              </p>
            ) : null}
            <Link
              to="/reporting?tab=money"
              className="inline-block mt-2 font-inter text-[11px] font-semibold text-gold-bright underline decoration-gold-dim/50 hover:decoration-gold-bright"
              data-testid="calc-turnover-report-link"
            >
              Matches Reporting · Money
            </Link>
          </div>
          <div className="panel p-5">
            <p className="label-small-caps inline-flex items-center gap-1">
              Ours (house)
              <InfoTip>The studio&apos;s share of the completed turnover after the artist commission split (the commission engine, as in Reporting &gt; Money).</InfoTip>
            </p>
            <p className="font-cinzel text-4xl leading-none mt-2 tabular-nums text-cream" data-testid="calc-ours-amount">
              {aud(turnover.oursAud)}
            </p>
            <p className="font-inter text-xs text-cream-soft mt-2">{oursPct}% of turnover</p>
          </div>
          <div className="panel p-5">
            <p className="label-small-caps inline-flex items-center gap-1">
              Artists
              <InfoTip>The artists&apos; share of the completed turnover after the split. What each artist is paid, including anyone still in the chair, is in the per-artist table below.</InfoTip>
            </p>
            <p className="font-cinzel text-4xl leading-none mt-2 tabular-nums text-cream" data-testid="calc-artists-amount">
              {aud(turnover.artistAud)}
            </p>
            <p className="font-inter text-xs text-cream-soft mt-2">share of completed turnover</p>
          </div>
        </div>

        {/* Also in today — merch (earned, all house) + deposits received (money in, not revenue).
            Each total toggles its own breakdown list below. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="calc-money-in">
          <button
            type="button"
            onClick={() => setShowMerch((v) => !v)}
            aria-expanded={showMerch}
            className="panel p-5 text-left w-full hover:border-gold-bright/50 transition"
            data-testid="calc-merch-toggle"
          >
            <p className="label-small-caps">Merch &amp; aftercare</p>
            <p className="font-cinzel text-3xl leading-none mt-2 tabular-nums text-cream">{aud(calc.merchAud)}</p>
            <p className="font-inter text-[10px] uppercase tracking-widest text-gold-bright mt-2">
              {showMerch ? 'Hide items ▴' : 'Show items ▾'}
            </p>
          </button>
          <button
            type="button"
            onClick={() => setShowDeposits((v) => !v)}
            aria-expanded={showDeposits}
            className="panel p-5 border-azure/40 text-left w-full hover:border-azure/70 transition"
            data-testid="calc-deposits-toggle"
          >
            <p className="label-small-caps text-azure">Deposits received</p>
            <p className="font-cinzel text-3xl leading-none mt-2 tabular-nums text-azure">{aud(depReceived)}</p>
            <p className="font-inter text-[10px] uppercase tracking-widest text-azure mt-2">
              {showDeposits ? 'Hide deposits ▴' : 'Show deposits ▾'}
            </p>
          </button>
        </div>

        {/* Breakdowns — revealed by clicking a total; what the merch + deposits totals are made of. */}
        {showMerch || showDeposits ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {showMerch ? (
              <div className="panel p-4" data-testid="calc-merch-breakdown">
                <p className="label-small-caps text-gold-bright mb-3 inline-flex items-center gap-1">
                  Merch &amp; aftercare &middot; {dayLabel(dayIso)}
                  <InfoTip>Merch and aftercare sold that day, per item — earned, all house (not split with artists).</InfoTip>
                </p>
                {merchRows.length === 0 ? (
                  <p className="font-serif text-cream-soft text-sm">No merch sold that day.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left">
                          <th className="font-inter text-[9px] uppercase tracking-widest text-cream-faint pb-2 pr-3">Item</th>
                          <th className="font-inter text-[9px] uppercase tracking-widest text-cream-faint pb-2 pr-3">Category</th>
                          <th className="font-inter text-[9px] uppercase tracking-widest text-cream-faint pb-2 pr-3 text-right">Qty</th>
                          <th className="font-inter text-[9px] uppercase tracking-widest text-cream-faint pb-2 text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {merchRows.map((r) => (
                          <tr key={r.itemId} className="border-t border-gold-dim/20" data-testid={`merch-row-${r.itemId}`}>
                            <td className="py-2 pr-3 text-cream">{r.name}</td>
                            <td className="py-2 pr-3 text-cream-faint text-[11px] uppercase tracking-wide">{r.category}</td>
                            <td className="py-2 pr-3 text-right font-mono tabular-nums text-cream-soft">{r.qty}</td>
                            <td className="py-2 text-right font-mono tabular-nums text-cream">{aud(r.totalAud)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : null}

            {showDeposits ? (
              <div className="panel p-4" data-testid="calc-deposits-breakdown">
                <p className="label-small-caps text-azure mb-3 inline-flex items-center gap-1">
                  Deposits received &middot; {dayLabel(dayIso)}
                  <InfoTip>New money in via deposits paid that day (money-in, not revenue). Excludes checkout rollovers.</InfoTip>
                </p>
                {depRows.length === 0 ? (
                  <p className="font-serif text-cream-soft text-sm">No deposits received that day.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left">
                          <th className="font-inter text-[9px] uppercase tracking-widest text-cream-faint pb-2 pr-3">Name</th>
                          <th className="font-inter text-[9px] uppercase tracking-widest text-cream-faint pb-2 pr-3">Channel</th>
                          <th className="font-inter text-[9px] uppercase tracking-widest text-cream-faint pb-2 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {depRows.map((r) => (
                          <tr key={r.id} className="border-t border-gold-dim/20" data-testid={`dep-received-${r.id}`}>
                            <td className="py-2 pr-3 text-cream">{r.name}</td>
                            <td className="py-2 pr-3 text-cream-faint text-[11px] uppercase tracking-wide">
                              {CHANNEL_LABEL[r.channel]}
                            </td>
                            <td className="py-2 text-right font-mono tabular-nums text-azure">{aud(r.amountAud)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Per-artist table */}
        <div className="panel p-4" data-testid="calc-artist-table">
          <p className="label-small-caps text-gold-bright mb-3 inline-flex items-center gap-1">
            Per artist · {studio.name} · {dayLabel(dayIso)}
            <InfoTip>
              Each artist&apos;s completed sessions that day: gross, the commission split, when their envelope
              falls due, and the payout sign-off state — all read from the E2 payout ledger, never recomputed
              here. &ldquo;Paid by&rdquo; is the artist&apos;s own finish (their last committed session, clamped
              to the 6pm clock-off), not the end of the day: the cash has to be in the drawer as each one walks
              out, which is what the front-load steer on Payouts &amp; cash is for.
            </InfoTip>
          </p>
          {calc.rows.length === 0 ? (
            <p className="font-serif text-cream-soft text-sm">No commissionable sessions that day.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left">
                    <th className="font-inter text-[9px] uppercase tracking-widest text-cream-faint pb-2 pr-3">Artist</th>
                    <th className="font-inter text-[9px] uppercase tracking-widest text-cream-faint pb-2 pr-3">Arrangement</th>
                    <th className="font-inter text-[9px] uppercase tracking-widest text-cream-faint pb-2 pr-3">Paid by</th>
                    <th className="font-inter text-[9px] uppercase tracking-widest text-cream-faint pb-2 pr-3 text-right">Sessions</th>
                    <th className="font-inter text-[9px] uppercase tracking-widest text-cream-faint pb-2 pr-3 text-right">Gross</th>
                    <th className="font-inter text-[9px] uppercase tracking-widest text-cream-faint pb-2 pr-3 text-right">Commission</th>
                    <th className="font-inter text-[9px] uppercase tracking-widest text-cream-faint pb-2 pr-3 text-right">House</th>
                    <th className="font-inter text-[9px] uppercase tracking-widest text-cream-faint pb-2">Payout</th>
                  </tr>
                </thead>
                <tbody>
                  {calc.rows.map((r) => {
                    const meta = SIGN_META[r.signState];
                    const arrangement = arrangementByArtist.get(r.artistId);
                    return (
                      <tr key={r.artistId} className="border-t border-gold-dim/20" data-testid={`calc-row-${r.artistId}`}>
                        <td className="py-2 pr-3 text-cream">{r.artistName}</td>
                        <td className="py-2 pr-3 font-mono text-[12px] text-cream-soft">
                          {arrangement ? arrangementLabel(arrangement) : ''}
                        </td>
                        <td className="py-2 pr-3 font-mono text-[12px] text-cream-soft">
                          {r.finishIso ? finishTime(r.finishIso) : '—'}
                        </td>
                        <td className="py-2 pr-3 text-right font-mono tabular-nums text-cream-soft">{r.sessionCount}</td>
                        <td className="py-2 pr-3 text-right font-mono tabular-nums text-cream">{aud(r.grossAud)}</td>
                        <td className="py-2 pr-3 text-right font-mono tabular-nums text-cream">{aud(r.payoutAud)}</td>
                        <td className="py-2 pr-3 text-right font-mono tabular-nums text-cream-soft">{aud(r.houseAud)}</td>
                        <td className="py-2">
                          <span className={clsx('text-[10px]', meta.pill)}>{meta.label}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* W7 n4: artist pay not yet handed over, network-wide, as at this day. */}
        <OutstandingPayPanel owed={owed} studios={studios} />

        {/* Ranged views moved to the Money report — the Day sheet stays one day */}
        <div className="panel p-4" data-testid="daysheet-report-pointer">
          <p className="label-small-caps text-gold-bright mb-2">Looking for the week, or when cash lands?</p>
          <p className="font-serif text-cream-soft text-sm max-w-2xl">
            The running weekly tally, the deposit pool and the settlement cash-timing strip are ranged, cross-day
            views, so they now live in the{' '}
            <Link
              to="/reporting?tab=money"
              className="text-gold-bright underline decoration-gold-dim/50 hover:decoration-gold-bright"
            >
              Money report
            </Link>{' '}
            where the date-range control belongs. The Day sheet stays strictly one day.
          </p>
        </div>
      </div>
    </section>
  );
}
